import { ServiceOpts, useClient } from "./client";
import type { Client } from "./gen/client/types.gen";
import * as services from "./gen/sdk.gen";

export type * as types from "./gen/types.gen";

type ServiceFunction<T> = T extends (options: infer O) => unknown
    ? O extends { client?: Client }
        ? (options: Omit<O, "client">) => ReturnType<T>
        : T
    : T;

type Services = Omit<
    {
        [K in keyof typeof services]: ServiceFunction<(typeof services)[K]>;
    },
    "client"
>;

/**
 * Use the services with our own client.
 */
const useServices = ({
    opts,
    requires,
    overrides,
}: {
    opts?: {
        throwOnError: boolean;
    };
} & ServiceOpts): Services => {
    const client = useClient({ requires, overrides });

    // Set default values for opts.
    if (!opts) {
        opts = { throwOnError: true };
    }
    if (opts?.throwOnError === undefined) {
        opts.throwOnError = true;
    }

    return Object.fromEntries(
        Object.entries(services as Services).map(([key, service]) => [
            key,

            (options: Record<string, unknown>) =>
                service({ client, ...opts, ...options }),
        ])
    ) as Services;
};

/**
 * Default services.
 */
export const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

// Re-exported from the leaf ./errors so the many modules that import them
// from here keep working, without this module being on their critical path.
export { isHttpExceptionModel, isHttpValidationError } from "./errors";
export { useClient, useServices };
